import APIMiddleware from '../../../data/api/Api-Middleware.js';
import i18n from '../../../i18n/index.js';
import {
  API_ENDPOINT_EA_SETTINGS,
  API_ENDPOINT_EA_INDICATORS,
  API_ENDPOINT_EA_INDICATORS_PREVIEW,
  API_ENDPOINT_EA_STRATEGIES,
  API_ENDPOINT_EA_CREATE_STRATEGY,
  API_ENDPOINT_EA_UPDATE_STRATEGY,
  API_ENDPOINT_EA_ENABLE_STRATEGY,
  API_ENDPOINT_EA_DISABLE_STRATEGY,
  API_ENDPOINT_EA_DELETE_STRATEGY,
  API_ENDPOINT_EA_RUNTIME,
  API_ENDPOINT_EA_CLOSE_POSITION,
} from '../../../data/Endpoints-API.js';

/**
 * The server answers every EA route with `{ data }` on success and `{ message }` on
 * failure, and the failure arrives as a rejected axios error. Unwrapping both here
 * keeps the components free of `e?.response?.data?.message` chains.
 *
 * Stop/close failures used to surface raw engine text ("1/1 closes failed",
 * "left unmanaged"). Map those to a short action the user can take.
 */
function localizeEaServerMessage(msg) {
  if (!msg || typeof msg !== 'string') return null;
  const text = msg.trim();
  if (text === 'No open position for this strategy') {
    return i18n.t('ea.noOpenPositionForStrategy');
  }
  if (
    /^Cannot stop\b/i.test(text) ||
    (/could not be closed/i.test(text) && /unmanaged|closes failed/i.test(text))
  ) {
    return i18n.t('ea.cannotStopUnmanaged');
  }
  return null;
}

export function eaErrorMessage(e, fallback = 'Request failed') {
  const raw =
    e?.response?.data?.message ||
    e?.response?.data?.error ||
    e?.message ||
    '';
  return localizeEaServerMessage(raw) || raw || fallback;
}

export const eaApi = {
  settings: () => APIMiddleware.get(API_ENDPOINT_EA_SETTINGS()).then((r) => r?.data || null),

  indicators: () => APIMiddleware.get(API_ENDPOINT_EA_INDICATORS()).then((r) => r?.data || []),

  /** Live/synthetic series preview for builder parity with AmChart values. */
  previewIndicator: (body) =>
    APIMiddleware.post(API_ENDPOINT_EA_INDICATORS_PREVIEW(), body).then((r) => r?.data || null),

  list: (userId) => APIMiddleware.get(API_ENDPOINT_EA_STRATEGIES(userId)).then((r) => r?.data || []),

  create: (payload) =>
    APIMiddleware.post(API_ENDPOINT_EA_CREATE_STRATEGY(), payload).then((r) => r?.data),

  update: (id, payload) =>
    APIMiddleware.put(API_ENDPOINT_EA_UPDATE_STRATEGY(id), payload).then((r) => r?.data),

  start: (id, userId) =>
    APIMiddleware.post(API_ENDPOINT_EA_ENABLE_STRATEGY(id), { userId }).then((r) => r?.data),

  stop: (id, userId) =>
    APIMiddleware.post(API_ENDPOINT_EA_DISABLE_STRATEGY(id), { userId }).then((r) => r?.data),

  remove: (id, userId) => APIMiddleware.delete(API_ENDPOINT_EA_DELETE_STRATEGY(id, userId)),

  runtime: (id, userId, filters) =>
    APIMiddleware.get(API_ENDPOINT_EA_RUNTIME(id, userId, filters)).then((r) => r?.data),

  closePosition: (id, userId) =>
    APIMiddleware.post(API_ENDPOINT_EA_CLOSE_POSITION(id), { userId }).then((r) => r?.data),
};

/**
 * Dispatch on window.top so events cross the amCharts iframe boundary.
 * EaChartControl lives inside the amCharts iframe and listens on its own window;
 * StrategiesTab lives in the main document — window.top is the common ancestor.
 */
const topWin = (() => { try { return window.top; } catch { return window; } })();

/** Group EA toggle / group-change — refresh show-hide via socket eaSettingsChanged. */
export const EA_SETTINGS_CHANGED_EVENT = 'ea:settings-changed';
export const eaNotifySettingsChanged = () =>
  topWin.dispatchEvent(new Event(EA_SETTINGS_CHANGED_EVENT));

/**
 * Strategy CRUD / entry-exit signals — refresh list + journal via socket eaStrategiesChanged
 * (and local eaNotifyChanged after same-tab mutations). No XHR polling.
 */
export const EA_CHANGED_EVENT = 'ea:strategies-changed';
export const eaNotifyChanged = () => topWin.dispatchEvent(new Event(EA_CHANGED_EVENT));

export const EA_OPEN_MODAL_EVENT = 'ea:open-builder';
export const eaOpenBuilder = (detail) =>
  topWin.dispatchEvent(new CustomEvent(EA_OPEN_MODAL_EVENT, { detail }));

/** Chart.jsx fires these around StockToolbar create/dispose so the Strategy
 *  button can leave #chartcontrols before amCharts rebuilds it (symbol, TF,
 *  style colours, bullets, reconnect). */
export const EA_CHART_TOOLBAR_TEARDOWN = 'ea:chart-toolbar-teardown';
export const EA_CHART_TOOLBAR_READY = 'ea:chart-toolbar-ready';
export const eaChartToolbarTeardown = () => {
  try { window.dispatchEvent(new Event(EA_CHART_TOOLBAR_TEARDOWN)); } catch { /* */ }
};
export const eaChartToolbarReady = () => {
  try { window.dispatchEvent(new Event(EA_CHART_TOOLBAR_READY)); } catch { /* */ }
};
